// ============================================
// Seed Templates: Trust & IP Documents
// Category: trust-ip
// Templates: 8
// ============================================

import type { DocTemplate, QualityGate } from '../../types/docops';

const IP_ASSIGNMENT_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'ipa-parties', title: 'Assignor/Assignee Identification', required: true, keywords: ['assignor', 'assignee', 'trust'], weight: 'critical' },
    { id: 'ipa-assets', title: 'IP Assets Description', required: true, keywords: ['software', 'source code', 'trademark', 'brand', 'intellectual property'], weight: 'critical' },
    { id: 'ipa-irrevocable', title: 'Irrevocable and Unconditional', required: true, keywords: ['irrevocable', 'unconditional'], weight: 'critical' },
    { id: 'ipa-no-encumbrance', title: 'No Prior Encumbrance', required: true, keywords: ['encumbrance', 'free and clear', 'sole creator', 'sole owner'], weight: 'critical' },
    { id: 'ipa-derivatives', title: 'Derivative Works Included', required: true, keywords: ['derivative', 'modifications', 'improvements', 'enhancements'], weight: 'critical' },
    { id: 'ipa-further-docs', title: 'Further Documents Obligation', required: true, keywords: ['further', 'document', 'cooperation', 'execute'], weight: 'high' },
    { id: 'ipa-no-retained', title: 'No Retained Rights', required: true, keywords: ['no retained', 'retains no', 'all rights'], weight: 'high' },
    { id: 'ipa-exhibit', title: 'IP Asset Schedule (Exhibit A)', required: true, keywords: ['exhibit', 'schedule', 'asset schedule'], weight: 'high' },
  ],
};

const IP_LICENSE_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'ipl-exclusive', title: 'Exclusive License Grant', required: true, keywords: ['exclusive', 'license', 'grant', 'field'], weight: 'critical' },
    { id: 'ipl-royalty', title: 'Royalty Terms', required: true, keywords: ['royalty', 'gross revenue', 'percent', 'monthly'], weight: 'critical' },
    { id: 'ipl-coc', title: 'Change of Control (Most Critical)', required: true, keywords: ['change of control', 'terminates', '90 day', 'licensor consent'], weight: 'critical' },
    { id: 'ipl-improvements', title: 'Improvements Owned by Licensor', required: true, keywords: ['improvement', 'owned by licensor', 'licensor owns', 'licensed back'], weight: 'critical' },
    { id: 'ipl-termination', title: 'Termination Provisions', required: true, keywords: ['termination', 'terminate', 'breach', 'cure'], weight: 'critical' },
    { id: 'ipl-reversion', title: 'IP Reversion on Termination', required: true, keywords: ['revert', 'return', 'upon termination', 'reverts'], weight: 'critical' },
    { id: 'ipl-renewal', title: 'Renewal Terms', required: true, keywords: ['renewal', 'auto-renew', 'initial term', '5-year', 'five'], weight: 'high' },
    { id: 'ipl-audit', title: 'Annual Audit Rights', required: true, keywords: ['audit', 'inspection', 'records', 'annual'], weight: 'high' },
    { id: 'ipl-noncompete', title: 'Post-Termination Non-Compete', required: true, keywords: ['non-compete', 'noncompete', '24 month', 'twenty-four'], weight: 'high' },
    { id: 'ipl-reps', title: 'Representations and Warranties', required: true, keywords: ['represent', 'warrant', 'representation'], weight: 'high' },
    { id: 'ipl-escrow', title: 'Source Code Escrow', required: true, keywords: ['escrow', 'source code', 'deposit'], weight: 'high' },
    { id: 'ipl-liability', title: 'Liability Limits', required: true, keywords: ['liability', 'limitation of liability', 'cap', 'indemnif'], weight: 'high' },
  ],
};

export const SEED_TEMPLATES_TRUST_IP: DocTemplate[] = [
  // ── 3.1 Family Trust Agreement ──
  {
    id: '3.1-trust-agreement',
    categoryId: 'trust-ip',
    title: 'Family Trust Agreement',
    description:
      'Revocable living trust agreement establishing the trust that holds all intellectual property related to the health technology platform.',
    templateContent: `# Family Trust Agreement

**{{trustName}}**
Trustee: {{trustTrusteeName}}

## Article I — Trust Purpose
{{AI_GENERATED_CONTENT}}

## Article II — Trustee Powers
{{AI_GENERATED_CONTENT}}

## Article III — Beneficiary Designation
{{AI_GENERATED_CONTENT}}

## Article IV — IP Asset Schedule
{{AI_GENERATED_CONTENT}}

## Article V — Amendment and Revocation
{{AI_GENERATED_CONTENT}}

## Article VI — Successor Trustee
{{AI_GENERATED_CONTENT}}

## Article VII — Distributions
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: ['trustName', 'trustTrusteeName'],
    generationPrompt:
      'Generate outline for a revocable living trust agreement for {{trustName}} with {{trustTrusteeName}} as trustee. Trust will hold all intellectual property related to health technology platforms. Outline key provisions: trust purpose, trustee powers, beneficiary designation, IP asset schedule, amendment/revocation rights, successor trustee, distributions. Format: outline for attorney review.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 15,
    sortOrder: 1,
    generationRound: 1,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 3.2 IP Assignment Agreement ──
  {
    id: '3.2-ip-assignment',
    categoryId: 'trust-ip',
    title: 'IP Assignment Agreement',
    qualityGate: IP_ASSIGNMENT_QUALITY_GATE,
    description:
      'Irrevocable assignment of all platform intellectual property from the founder to the family trust. Includes software, AI engine, clinical protocols, brand assets, and all derivative works.',
    templateContent: `# IP Assignment Agreement

Assignor: [Founder — Individual]
Assignee: {{trustName}}
Trustee: {{trustTrusteeName}}

Effective Date: {{effectiveDate}}

## 1. Assignment of IP
{{AI_GENERATED_CONTENT}}

## 2. Assigned Assets
All {{brandName}} intellectual property including but not limited to:
{{AI_GENERATED_CONTENT}}

## 3. Representations and Warranties
{{AI_GENERATED_CONTENT}}

## 4. Further Assurances
{{AI_GENERATED_CONTENT}}

## Exhibit A — IP Asset Schedule
{{AI_GENERATED_CONTENT}}

_________________________
Assignor Signature

_________________________
Trustee Signature ({{trustTrusteeName}})
`,
    requiredVariables: [
      'trustName',
      'trustTrusteeName',
      'brandName',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate IP Assignment Agreement OUTLINE for attorney review. Assignor: [Founder] (individual), Assignee: {{trustName}} (family trust), Trustee: {{trustTrusteeName}}. Assets: all {{brandName}} IP including software source code, AI analysis engine, clinical protocols, supplement formulary, biomarker research, brand/trademarks/logos, domains, marketing content, educational content, dashboard designs, compliance system architectures. Provisions: irrevocable/unconditional, sole creator/owner rep, no prior encumbrance, includes derivative works, further documents, no retained rights, Exhibit A IP Asset Schedule. Format: outline with attorney notes, draft Exhibit A structure.',
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 8,
    sortOrder: 2,
    generationRound: 1,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 3.3 IP Asset Schedule (Exhibit A) ──
  {
    id: '3.3-ip-asset-schedule',
    categoryId: 'trust-ip',
    title: 'IP Asset Schedule (Exhibit A)',
    description:
      'Comprehensive inventory of all intellectual property assets for the platform, organized by category. Referenced by the IP Assignment Agreement and IP License Agreement.',
    templateContent: `# IP Asset Schedule — Exhibit A

**{{brandName}}**

## 1. Software Assets
| Asset | Description | Status |
|-------|-------------|--------|
{{AI_GENERATED_CONTENT}}

## 2. AI/ML Assets
{{AI_GENERATED_CONTENT}}

## 3. Clinical Assets
{{AI_GENERATED_CONTENT}}

## 4. Brand Assets
{{AI_GENERATED_CONTENT}}

## 5. Content Assets
{{AI_GENERATED_CONTENT}}

## 6. Data Assets
{{AI_GENERATED_CONTENT}}

## 7. Systems & Architecture
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: ['brandName'],
    generationPrompt:
      'Generate a comprehensive IP Asset Schedule for {{brandName}} covering: 1) Software (frontend React/Vite/TypeScript, backend Firebase Cloud Functions, AI analysis engine, admin dashboards), 2) AI/ML Assets (lab analysis methodology, biomarker interpretation framework, protocol builder logic, archetype classification), 3) Clinical (44 biomarker ranges, supplement formulary, clinical tier assessment), 4) Brand (trademarks, logos, domain names, visual identity, color palette, typography), 5) Content (educational health content, chat scripts, marketing copy), 6) Data (biomarker research data, functional medicine ranges), 7) Systems (compliance architecture, data ops framework, consent management system). Format: detailed inventory table.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 5,
    sortOrder: 3,
    generationRound: 1,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 3.4 IP License Agreement ──
  {
    id: '3.4-ip-license-agreement',
    categoryId: 'trust-ip',
    title: 'IP License Agreement',
    qualityGate: IP_LICENSE_QUALITY_GATE,
    description:
      'Exclusive license of all platform IP from the trust to the operating LLC. Contains the critical Change of Control clause — license terminates on unauthorized change of control, protecting the founder.',
    templateContent: `# Exclusive IP License Agreement

Licensor: {{trustName}}
Trustee: {{trustTrusteeName}}
Licensee: {{brandEntity}}
CEO: {{ceoName}}

Effective Date: {{effectiveDate}}

## 1. Licensed IP
All {{brandName}} intellectual property per the IP Asset Schedule.

## 2. Grant of License
{{AI_GENERATED_CONTENT}}

## 3. Field of Use
{{AI_GENERATED_CONTENT}}

## 4. Royalty
{{ipLicenseRoyaltyPercent}}% of gross revenue, payable monthly within 15 days.
{{AI_GENERATED_CONTENT}}

## 5. Term and Renewal
{{AI_GENERATED_CONTENT}}

## 6. CHANGE OF CONTROL ⚠️ CRITICAL CLAUSE
**License terminates 90 days post-Change of Control unless Licensor consents.**
{{AI_GENERATED_CONTENT}}

## 7. Improvements
{{AI_GENERATED_CONTENT}}

## 8. Termination by Licensor
{{AI_GENERATED_CONTENT}}

## 9. Termination by Licensee
{{AI_GENERATED_CONTENT}}

## 10. Effect of Termination
{{AI_GENERATED_CONTENT}}

## 11. Audit Rights
{{AI_GENERATED_CONTENT}}

## 12. Non-Compete
{{AI_GENERATED_CONTENT}}

## 13. Representations and Warranties
{{AI_GENERATED_CONTENT}}

## 14. Limitation of Liability
{{AI_GENERATED_CONTENT}}

## 15. Dispute Resolution
{{AI_GENERATED_CONTENT}}

## 16. Source Code Escrow
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'trustName',
      'trustTrusteeName',
      'brandEntity',
      'brandName',
      'ceoName',
      'marketVertical',
      'ipLicenseRoyaltyPercent',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate Exclusive IP License Agreement OUTLINE for attorney review. Licensor: {{trustName}}, Trustee: {{trustTrusteeName}}, Licensee: {{brandEntity}}, CEO: {{ceoName}}. Licensed IP: all {{brandName}} IP per IP Asset Schedule. Field: AI-powered health analysis, supplement recommendation, provider consultation in {{marketVertical}}. Terms: 1) Exclusive license in field, 2) Royalty {{ipLicenseRoyaltyPercent}}% of gross revenue monthly within 15 days, 3) Minimum royalty floor, 4) 5-year initial + 3-year auto-renewal, 5) CHANGE OF CONTROL: license terminates 90 days post-CoC unless Licensor consents (THIS IS THE MOST IMPORTANT CLAUSE), 6) Improvements during term owned by Licensor, licensed back, 7) Termination by Licensor (non-payment 60d, breach 30d cure, bankruptcy, unauthorized CoC, reputational harm), 8) Termination by Licensee (90d notice), 9) IP reverts on termination, 10) Annual audit rights, 11) Non-compete 24mo post-termination, 12) Reps/warranties, 13) Liability limits, 14) Arbitration, 15) Source code escrow. Format: detailed outline with commentary, flag Change of Control as most critical.',
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 15,
    sortOrder: 4,
    generationRound: 1,
    dependencies: [],
    anchorSections: ['§7'],
    inheritedProvisions: ['ip-licensed-model'],
  },

  // ── 3.5 IP Valuation Report ──
  {
    id: '3.5-ip-valuation',
    categoryId: 'trust-ip',
    title: 'IP Valuation Report',
    description:
      'Framework for valuing the platform intellectual property using cost, market, and income approaches. Used for trust administration and potential licensing negotiations.',
    templateContent: `# IP Valuation Report

**{{brandName}}** IP held by {{trustName}}

## Executive Summary
{{AI_GENERATED_CONTENT}}

## 1. Cost Approach
{{AI_GENERATED_CONTENT}}

## 2. Market Approach
{{AI_GENERATED_CONTENT}}

## 3. Income Approach
{{AI_GENERATED_CONTENT}}

## 4. Valuation Factors
{{AI_GENERATED_CONTENT}}

## 5. Conclusion
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: ['brandName', 'trustName'],
    generationPrompt:
      'Generate an IP Valuation framework for {{brandName}} IP held by {{trustName}}. Methods: 1) Cost approach (development costs to recreate), 2) Market approach (comparable health-tech platform acquisitions), 3) Income approach (projected royalty income from license). Include valuation factors: AI analysis engine uniqueness, formulary curation, regulatory compliance infrastructure, brand recognition, market position. Format: structured report template with sections for each method.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 5,
    generationRound: 5,
    dependencies: ['3.4-ip-license-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 3.6 Trust Amendment Template ──
  {
    id: '3.6-trust-amendment-template',
    categoryId: 'trust-ip',
    title: 'Trust Amendment Template',
    description:
      'Reusable template for amending trust provisions. Used when trust terms need modification without full restatement.',
    templateContent: `# Amendment to {{trustName}}

Amendment Number: [____]
Date: [____]

Trustee: {{trustTrusteeName}}

## Provisions Being Modified
{{AI_GENERATED_CONTENT}}

## New Language
{{AI_GENERATED_CONTENT}}

## Effective Date
{{AI_GENERATED_CONTENT}}

_________________________
Trustee Signature ({{trustTrusteeName}})

Date: ___________________________
`,
    requiredVariables: ['trustName', 'trustTrusteeName'],
    generationPrompt:
      'Generate a reusable Trust Amendment template for {{trustName}} (Trustee: {{trustTrusteeName}}). Template for modifying trust provisions — amendment number, date, specific provisions being modified, new language, effective date, trustee signature. Format: clean legal template.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 6,
    generationRound: 6,
    dependencies: ['3.1-trust-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 3.7 IP Audit Checklist ──
  {
    id: '3.7-ip-audit-checklist',
    categoryId: 'trust-ip',
    title: 'IP Audit Checklist',
    description:
      'Annual checklist for auditing all intellectual property assets including trademark renewals, domain renewals, trade secret protections, and third-party IP clearance.',
    templateContent: `# Annual IP Audit Checklist

**{{brandName}}**

| # | Category | Task | Responsible Party | Due Date | Status |
|---|----------|------|-------------------|----------|--------|
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: ['brandName'],
    generationPrompt:
      'Generate an annual IP Audit Checklist for {{brandName}}. Categories: trademark registrations/renewals, domain name renewals, software copyright registrations, trade secret protection measures, license compliance verification, new IP identification, IP asset schedule updates, third-party IP clearance, employee/contractor IP assignment confirmation. Format: checklist with responsible party and due date columns.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 7,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 3.8 Source Code Escrow Agreement ──
  {
    id: '3.8-source-code-escrow',
    categoryId: 'trust-ip',
    title: 'Source Code Escrow Agreement',
    description:
      'Agreement for depositing source code with a third-party escrow agent, with release conditions protecting the licensee in case of depositor default.',
    templateContent: `# Source Code Escrow Agreement

Depositor: {{trustName}}
Beneficiary: {{brandEntity}}
Escrow Agent: [Third-Party Escrow Agent]

## 1. Deposit Requirements
{{AI_GENERATED_CONTENT}}

## 2. Release Conditions
{{AI_GENERATED_CONTENT}}

## 3. Verification
{{AI_GENERATED_CONTENT}}

## 4. Term and Fees
{{AI_GENERATED_CONTENT}}

## 5. Confidentiality
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: ['brandEntity', 'trustName'],
    generationPrompt:
      'Generate outline for Source Code Escrow Agreement between {{brandEntity}} (Licensee/Beneficiary) and {{trustName}} (Depositor) with a third-party escrow agent. Release conditions: Depositor bankruptcy, material breach of license agreement, failure to maintain source code. Deposit requirements: quarterly deposits of complete source code, build instructions, documentation. Format: outline for attorney review.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 5,
    sortOrder: 8,
    generationRound: 5,
    dependencies: ['3.4-ip-license-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
